const MAX_TEXT_LENGTH = 11;
class ProductCustomizer extends HTMLElement {
    constructor() {
        super();
        this.currentStep = 'text';
        this.availableSteps = ['text'];
        this.hasText = false;
        this.isDesktop = window.innerWidth > 768;
        this.isProgrammaticScroll = false;
        this.scrollThrottle = null;
        this.isBulkMode = false;
        this.currentQuantity = 1;
        this.isProcessingNext = false; 

        // Single handler for ALL option clicks
        this._clickHandlerBound = null;
        this._lastOptionUpdate = 0;
        this._optionUpdateTimeout = null;
    }

    connectedCallback() {            
        this.isBulkMode = !!this.closest('bulk-personalization');        
        this.variantId = this.dataset.initialVariantId;
    
        const variantsImgScript = document.body.querySelector("#variantPersonalizationImage");
        this.variantsPersonalizationImages = JSON.parse(variantsImgScript?.textContent || '{}');
    
        const variantsRegionScript = document.body.querySelector("#variantCustomizableRegion");
        this.regionScriptData = JSON.parse(variantsRegionScript?.textContent || '{}');
        
        // BULK MODE: Skip single personalization event setup
        if (this.isBulkMode) {
            this.setupBulkModeBasicsOnly();
            return; // Stop here completely for bulk mode
        }
        const variantsDataScript = document.getElementById('allVariantsData');
        if (variantsDataScript) {
            this.allVariantsInventory = JSON.parse(variantsDataScript.textContent);
        }
        
        // SINGLE MODE: Continue with normal setup
        this.selectElements();
        this.setupAvailableSteps();
        this.initialDataObjects();
        this.setupAllCanvases();
        this.setupCanvasEvents();
        this.setupAllEvents();
        this.setupStepNavigation();
        this.setupMobileTabs();
        this.setupScrollEvents();

        this.debugStepTransitions();
        setTimeout(() => {
            this.debugScrollContainer();
        }, 1000);
        
        this.currentStep = 'text';        
        setTimeout(() => {
            this.updateNextButtonState();
            
            setTimeout(() => {                
                this.updateNextButtonState();
            }, 200);
        }, 100);
        
        setTimeout(() => {    
            this.hasText = false;
            this.savedCustomizationData["personalized-text"] = "";
            this.updateNextButtonState();
            
            if (this.desktopNextButton) {
                this.desktopNextButton.disabled = true;
                this.desktopNextButton.setAttribute('disabled', 'disabled');
            }
            
            this.mobileNextButtons.forEach(button => {
                button.disabled = true;
                button.setAttribute('disabled', 'disabled');
            });
        }, 500);
    }

    setupBulkModeBasicsOnly() {
        // Only setup the absolute essentials for bulk mode
        this.selectElements();
        this.initialDataObjects();
        this.setupAllCanvases();
    }

    showSinglePersonalization() {
        const bulkPersonalization = document.querySelector('bulk-personalization');
        if (bulkPersonalization && bulkPersonalization.resetBulkPersonalization) {
            bulkPersonalization.resetBulkPersonalization();
        }
        
        this.classList.remove('hidden-block');
    }

    showBulkPersonalization() {
        this.classList.add('hidden-block');
        
        const bulkPersonalization = document.querySelector('bulk-personalization');
        if (bulkPersonalization) {
            bulkPersonalization.classList.remove('hidden-block');
        }
    }

    shouldHandleEvent(event) {
        if (this.closest('bulk-personalization')) {
            return false;
        }
        return true;
    }

    setupBulkMode() {
        const allCanvases = this.querySelectorAll('canvas');
        allCanvases.forEach(canvas => {
            canvas.style.display = 'block';
        });
        
        if (this.mainCanvas) {
            this.mainCanvas.style.display = 'block';
        }
        
        this.allSteps.forEach(step => {
            step.style.display = '';
        });
    }
    
    forceUpdateButtonState() {
        this.updateNextButtonState();
    }

    selectElements() {
        this.mainCanvas = this.querySelector("#mainPersonalizationCanvas");
        
        this.canvas = this.querySelector("#personalizationCanvas");
        this.canvasFontStyle = this.querySelector("#personalizationCanvasFontStyle");
        this.canvasFontPattern = this.querySelector("#personalizationCanvasFontPattern");
        this.canvasIcon = this.querySelector("#personalizationCanvasIcon");
        this.canvasPreview = this.querySelector("#personalizationCanvasPreview");

        this.imageUrl = this.variantsPersonalizationImages[this.variantId];        
        this.productImg = new Image();
        this.productImg.crossOrigin = "anonymous";
        this.productImg.src = this.imageUrl;

        this.fontStyleOptions = this.querySelectorAll('input[name="font-style"]');
        this.fontPatternOptions = this.querySelectorAll('input[name="font-pattern"]');
        this.iconOptions = this.querySelectorAll('input[name="icon"]');

        this.addUniqueClassesToOptionLabels();

        this.allSteps = this.querySelectorAll(".personalization-step");
    
        this.desktopNextButton = this.querySelector(".sticky-contents .desktop-step-navigation .step-next");
        this.mobileNextButtons = this.querySelectorAll(".mobile-sticky-btns .mobile-step-navigation .step-next");    
        this.textInput = this.querySelector("#personalizedText");

        if (this.textInput) {
            const initialText = this.textInput.value.trim();
            this.hasText = initialText.length > 0;
        } else {
            this.hasText = false;
            console.warn('⚠️ Text input not found');
        }
    
        this.textOrientations = this.querySelectorAll("button.text-orientation__option");
        
        this.tabContainers = this.querySelectorAll('.mobile-tab-navigation');
        this.allTabButtons = this.querySelectorAll('.tab-button');

        this.previewText = this.querySelector("#previewText");
        this.previewFontStyle = this.querySelector("#previewFontStyle");
        this.previewFontPattern = this.querySelector("#previewFontPattern");
        this.previewIcon = this.querySelector("#previewIcon");
        
        this.addToCartBtn = this.querySelector("button.addtocart--button");
        this.editCustomizationBtn = this.querySelector(".edit-customization-btn");
        
        this.scrollContainer = this.querySelector('.desktop-scroll-container');
        
        this.customizationContainer = this.querySelector('.customization-steps-container');
        this.previewContainer = this.querySelector('.preview-step-container');
        this.staticImageContainer = this.querySelector('.static-image-container');

        this.quantityInputs = this.querySelectorAll('.quantity-input');
        this.initQuantityInputs();
    }

    initQuantityInputs() {
        this.quantityInputs.forEach(input => {
            input.value = this.currentQuantity;
        });
    }
    
    addUniqueClassesToOptionLabels() {
        // Update each option group
        this.updateOptionClasses('font-style');
        this.updateOptionClasses('font-pattern');
        this.updateOptionClasses('icon');
    }

    removeUniqueClassesFromAllLabels() {       
        const allLabels = this.querySelectorAll('label');
        allLabels.forEach(label => {
            label.classList.remove(
                'font-style-option-label', 
                'font-style-option-selected',
                'font-pattern-option-label', 
                'font-pattern-option-selected',
                'icon-option-label', 
                'icon-option-selected'
            );
        });
    }

    // Add this method to ProductCustomizer class
    setupBulkModeIsolation() {
        if (!this.isBulkMode) return;
        
        // Remove all single personalization event listeners that might interfere
        this.cleanupSingleModeEvents();
        
        // Set up bulk-specific handlers that won't conflict
        this.setupBulkOnlyEventHandlers();
    }

    cleanupSingleModeEvents() {
        // Remove event listeners that might interfere with bulk mode
        const elementsToClean = [
            '.tab-button',
            'label[for*="font-style"]',
            'label[for*="font-pattern"]', 
            'label[for*="icon"]',
            'input[name="font-style"]',
            'input[name="font-pattern"]',
            'input[name="icon"]'
        ];
        
        elementsToClean.forEach(selector => {
            const elements = this.querySelectorAll(selector);
            elements.forEach(element => {
                const newElement = element.cloneNode(true);
                element.parentNode.replaceChild(newElement, element);
            });
        });
    }

    setupBulkOnlyEventHandlers() {
        // Only set up handlers that are safe for bulk mode
        if (this.textInput) {
            const newTextInput = this.textInput.cloneNode(true);
            this.textInput.parentNode.replaceChild(newTextInput, this.textInput);
            this.textInput = newTextInput;
            
            this.textInput.addEventListener("input", 
                this.debounce((e) => this.handleBulkTextInput(e), 200)
            );
        }
        
        // Orientation buttons - safe for bulk
        if (this.textOrientations.length > 0) {
            this.textOrientations.forEach((btn) => {
                const newBtn = btn.cloneNode(true);
                btn.parentNode.replaceChild(newBtn, btn);
            });
            
            this.textOrientations = this.querySelectorAll("button.text-orientation__option");
            
            this.textOrientations.forEach((btn) => {
                btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    this.handleBulkOrientationChange(btn);
                });
            });
        }
    }

    setupAvailableSteps() {
        this.availableSteps = ['text'];
        
        const stepsToCheck = [
            { step: 'font-style', selector: '.personalization-step[data-step="font-style"]' },
            { step: 'font-pattern', selector: '.personalization-step[data-step="font-pattern"]' },
            { step: 'icon', selector: '.personalization-step[data-step="icon"]' }
        ];
        
        stepsToCheck.forEach(({ step, selector }) => {
            const element = this.querySelector(selector);
            if (element) {
                this.availableSteps.push(step);
            } else {
                console.log(`⏭️ Step "${step}" not found, skipping`);
            }
        });
        
        this.availableSteps.push('preview');
        this.validateStepConfiguration();
    }

    initialDataObjects() {
        this.defaultOrientationButton = Array.from(this.textOrientations).find(btn => btn.classList.contains("active"));
        
        const defaultFontStyleOption = this.fontStyleOptions?.[0];
    
        const currentText = this.textInput ? this.textInput.value.trim() : "";
        this.hasText = currentText.length > 0;
    
        this.personalizationProperties = {
            "personalized-text": currentText,
            "text-orientation": `${this.defaultOrientationButton?.dataset?.role || 'horizontal'}`,
            "font-style-label": `${defaultFontStyleOption?.dataset.optionLabel || ''}`,
            "font-pattern-label": "",
            "icon-label": ""
        }
    
        this.savedCustomizationData = {
            "personalized-text": currentText,
            "text-orientation": `${this.defaultOrientationButton?.dataset?.role || 'horizontal'}`,
            "font-style": `${defaultFontStyleOption?.dataset.optionData || ''}`,
            "font-style-label": `${defaultFontStyleOption?.dataset.optionLabel || ''}`,
            "font-pattern": "",
            "font-pattern-label": "",
            "icon": "",
            "icon-label": "",
        };
    }

    setupAllCanvases() {
        if (this.mainCanvas) {
            const ctx = this.mainCanvas.getContext("2d");
            this.mainCanvas.width = 3000;
            this.mainCanvas.height = 3000;
            ctx.imageSmoothingEnabled = true;

            if (!this.isBulkMode) {
                this.mainCanvas.style.display = this.isDesktop ? 'block' : 'none';
            } else {
                this.mainCanvas.style.display = 'none';
            }
        }

        const canvases = [
            { canvas: this.canvas, name: 'canvas' },
            { canvas: this.canvasFontStyle, name: 'canvasFontStyle' },
            { canvas: this.canvasFontPattern, name: 'canvasFontPattern' },
            { canvas: this.canvasIcon, name: 'canvasIcon' },
            { canvas: this.canvasPreview, name: 'canvasPreview' }
        ];

        canvases.forEach(({ canvas, name }) => {
            if (canvas) {
                const ctx = canvas.getContext("2d");
                canvas.width = 3000;
                canvas.height = 3000;
                ctx.imageSmoothingEnabled = true;

                if (!this.isBulkMode) {
                    canvas.style.display = 'none';
                } else {
                    canvas.style.display = 'none';
                }
            }
        });

        this.customizationRegionData = this.convertRegionDataToCanvas(this.regionScriptData, this.mainCanvas || this.canvas);
        
        this.productImg.onload = () => {
            this.renderAllCanvases();
        };
    }
    
    setupBulkSpecificEvents() {
        if (!this.isBulkMode) return;        
        
        // Remove any existing event listeners first
        this.cleanupBulkEvents();
        
        // Set up specific event listeners for bulk mode
        this.setupBulkFontPatternEvents();
        this.setupBulkIconEvents();
        this.setupBulkFontStyleEvents();
    }
    
    cleanupBulkEvents() {
        // This will help remove any duplicate event listeners
        const fontPatternLabels = this.querySelectorAll('label:has(input[name="font-pattern"])');
        const iconLabels = this.querySelectorAll('label:has(input[name="icon"])');
        const fontStyleLabels = this.querySelectorAll('label:has(input[name="font-style"])');
        
        // Reclone all elements to remove existing event listeners
        [fontPatternLabels, iconLabels, fontStyleLabels].forEach(labelGroup => {
            labelGroup.forEach((label) => {
                const newLabel = label.cloneNode(true);
                label.parentNode.replaceChild(newLabel, label);
            });
        });
    }
    
    setupBulkFontPatternEvents() {        
        const fontPatternLabels = this.querySelectorAll('label:has(input[name="font-pattern"])');
        
        fontPatternLabels.forEach((label, index) => {
            const input = label.querySelector('input[name="font-pattern"]');
            
            if (!input) return;
            
            
            // Add bulk-specific font pattern click handler
            label.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();               
                
                this.handleBulkFontPatternSelection(input);
            });
        });
    }
    
    handleBulkFontPatternSelection(input) {
        const allInputs = this.querySelectorAll(`input[name="font-pattern"]`);
        const isCurrentlySelected = input.checked;        
        
        if (isCurrentlySelected) {
            // Deselect in bulk mode
            input.checked = false;
            this.savedCustomizationData["font-pattern"] = "";
            this.savedCustomizationData["font-pattern-label"] = "";
            this.personalizationProperties["font-pattern-label"] = "";
        } else {
            // Select this one - deselect all others first
            allInputs.forEach(opt => {
                opt.checked = false;
            });
            input.checked = true;
            this.savedCustomizationData["font-pattern"] = input.dataset.optionData;
            this.savedCustomizationData["font-pattern-label"] = input.dataset.optionLabel;
            this.personalizationProperties["font-pattern-label"] = input.dataset.optionLabel;
        }
        
        
        // Force immediate rendering with proper canvas handling
        this.renderAllCanvasesForBulk();
        
        // Dispatch event for bulk mode
        this.dispatchEvent(new CustomEvent('BulkCustomizerChange', {
            detail: {...this.savedCustomizationData}
        }));
        
        this.addUniqueClassesToOptionLabels();
        this.updatePreviewSummary();
    }
    
    setupBulkIconEvents() {
        const iconLabels = this.querySelectorAll('label:has(input[name="icon"])');
        
        iconLabels.forEach((label, index) => {
            const newLabel = label.cloneNode(true);
            label.parentNode.replaceChild(newLabel, label);
            
            const input = newLabel.querySelector('input[name="icon"]');
            
            if (!input) return;
            
            
            // Add bulk-specific icon click handler
            newLabel.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                
                // Use the existing logic but ensure it works in bulk mode
                this.handleBulkIconSelection(input);
            });
        });
    }
    
    setupBulkFontStyleEvents() { 
        const fontStyleLabels = this.querySelectorAll('label:has(input[name="font-style"])');
        
        fontStyleLabels.forEach((label, index) => {
            const newLabel = label.cloneNode(true);
            label.parentNode.replaceChild(newLabel, label);
            
            const input = newLabel.querySelector('input[name="font-style"]');
            
            if (!input) return;
                        
            // Add isolated font style click handler
            newLabel.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                
                
                this.handleBulkFontStyleSelection(input, newLabel);
            });
        });
    }
    
    handleBulkFontStyleSelection(input, labelElement) {        
        const allInputs = this.querySelectorAll(`input[name="font-style"]`);
        const allLabels = this.querySelectorAll('label:has(input[name="font-style"])');
        
        // Remove selected classes from all labels first
        allLabels.forEach(label => {
            label.classList.remove('font-style-option-selected');
        });
        
        // Font style is required, so we don't allow deselection
        allInputs.forEach(opt => {
            opt.checked = false;
        });
        input.checked = true;
        
        // Add selected class to the clicked label
        if (labelElement) {
            labelElement.classList.add('font-style-option-selected');
        }
        
        this.savedCustomizationData["font-style"] = input.dataset.optionData;
        this.savedCustomizationData["font-style-label"] = input.dataset.optionLabel;
        this.personalizationProperties["font-style-label"] = input.dataset.optionLabel;
        
        
        // IMPORTANT: Preserve font pattern and re-render
        if (this.closest('bulk-personalization')) {
            const bulkPersonalization = this.closest('bulk-personalization');
            if (bulkPersonalization.forceBulkCanvasUpdateWithText) {
                bulkPersonalization.forceBulkCanvasUpdateWithText(this);
            }
        } else {
            this.renderAllCanvases();
        }
        
        // Dispatch event for bulk mode
        this.dispatchEvent(new CustomEvent('BulkCustomizerChange', {
            detail: {...this.savedCustomizationData}
        }));
        
        this.addUniqueClassesToOptionLabels();
    }
    
    setupCanvasEvents() {
        const newTextInput = this.textInput.cloneNode(true);
        this.textInput.parentNode.replaceChild(newTextInput, this.textInput);
        this.textInput = newTextInput;
        
        this.textInput.addEventListener("input", 
            this.debounce((e) => this.handleTextInput(e), 200)
        );
        
        this.textOrientations.forEach((btn) => {
            const newBtn = btn.cloneNode(true);
            btn.parentNode.replaceChild(newBtn, btn);
        });
        
        this.textOrientations = this.querySelectorAll("button.text-orientation__option");
        
        this.textOrientations.forEach((btn) => {
            btn.addEventListener("click", (e) => {
                e.preventDefault();
                this.handleOrientationChange(btn);
            });
        });
        
        // Setup a SINGLE event handler for ALL option clicks (font-style, font-pattern, icon)
        this.setupUnifiedOptionClickHandler();
        
        if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
            FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
                this.handleVariantChange(data.variantId);
            });
        }
    }

    setupUnifiedOptionClickHandler() {
        // Clean up any existing handler
        if (this._clickHandlerBound) {
            this.removeEventListener('click', this._clickHandlerBound);
        }
        
        // Create a single handler for ALL option clicks
        this._clickHandlerBound = (e) => {
            // Only handle clicks in single mode
            if (this.isBulkMode) return;
            
            // Check if it's an option label click
            const label = e.target.closest(`
                label[for*="font-style"], 
                label[for*="font-pattern"], 
                label[for*="icon"],
                label:has(input[name="font-style"]),
                label:has(input[name="font-pattern"]),
                label:has(input[name="icon"])
            `);
            
            if (!label) return;
            
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            
            // Determine which option type was clicked
            let optionName = null;
            let input = null;
            
            // Check for nested input
            input = label.querySelector('input[type="radio"]');
            
            // If no nested input, check for "for" attribute
            if (!input && label.hasAttribute('for')) {
                const inputId = label.getAttribute('for');
                input = this.querySelector(`#${inputId}`);
            }
            
            if (!input) return;
            
            // Determine option type
            if (input.name === 'font-style') {
                optionName = 'font-style';
            } else if (input.name === 'font-pattern') {
                optionName = 'font-pattern';
            } else if (input.name === 'icon') {
                optionName = 'icon';
            }
            
            if (!optionName) return;
            
            // Throttle updates
            const now = Date.now();
            if (now - this._lastOptionUpdate < 100) {
                return;
            }
            this._lastOptionUpdate = now;
            
            // Clear any pending updates
            if (this._optionUpdateTimeout) {
                clearTimeout(this._optionUpdateTimeout);
            }
            
            // Process the click
            this._optionUpdateTimeout = setTimeout(() => {
                this.handleUnifiedOptionClick(input, optionName);
            }, 50);
        };
        
        // Attach with capture phase to ensure it runs first
        this.addEventListener('click', this._clickHandlerBound, true);
    }

    handleUnifiedOptionClick(input, optionName) {
        const allInputs = this.querySelectorAll(`input[name="${optionName}"]`);
        const isRequired = optionName === 'font-style';
        
        const optionLabel = input.dataset.optionLabel || "";
        const currentSavedLabel = this.savedCustomizationData[`${optionName}-label`];
        const isAlreadySelected = !!(currentSavedLabel && currentSavedLabel === optionLabel);
        
        // If it's already selected
        if (isAlreadySelected) {
            // Font-style is required, can't deselect
            if (isRequired) return;
            
            // Font-pattern should not be deselected
            if (optionName === 'font-pattern') return;
            
            // Only icons can be deselected
            if (optionName === 'icon') {
                input.checked = false;
                this.savedCustomizationData[optionName] = "";
                this.savedCustomizationData[`${optionName}-label`] = "";
                // Icon deselected: restore vertical orientation button visibility
                this.updateIconAndOrientationMutualExclusion(false);
            }
        } else {
            // Uncheck all other options of this type
            allInputs.forEach(opt => {
                opt.checked = false;
            });
            
            // Check the clicked one
            input.checked = true;
            
            // Update data
            this.savedCustomizationData[optionName] = input.dataset.optionData || "";
            this.savedCustomizationData[`${optionName}-label`] = input.dataset.optionLabel || "";
            
            // When an icon is selected, enforce horizontal orientation and hide vertical button
            if (optionName === 'icon') {
                const hasActualIcon = !!(input.dataset.optionData && input.dataset.optionData.trim() !== "");
                this.updateIconAndOrientationMutualExclusion(hasActualIcon);
            }
        }
        
        // Update visual classes
        this.updateOptionClasses(optionName);
        
        // Update UI
        this.renderAllCanvases();
        this.updateNextButtonState();
        this.updatePreviewSummary();
        this.dispatchSavedEvent();
    }

    /**
     * Enforces mutual exclusion between icon selection and vertical text orientation (single mode).
     * @param {boolean} iconSelected - true when an icon has just been selected
     */
    updateIconAndOrientationMutualExclusion(iconSelected) {
        if (iconSelected) {
            // Hide the vertical orientation button and force horizontal
            this.textOrientations.forEach(btn => {
                if (btn.dataset.role === 'vertical') {
                    btn.style.display = 'none';
                }
                if (btn.dataset.role === 'horizontal') {
                    btn.classList.add('active');
                    btn.style.display = '';
                }
            });
            // Reset orientation data to horizontal
            this.savedCustomizationData['text-orientation'] = 'horizontal';
            if (this.personalizationProperties) {
                this.personalizationProperties['text-orientation'] = 'horizontal';
            }
        } else {
            // Icon deselected: restore vertical orientation button visibility
            this.textOrientations.forEach(btn => {
                btn.style.display = '';
            });
        }
    }

    updateOptionClasses(optionName) {
        const inputs = this.querySelectorAll(`input[name="${optionName}"]`);
        const selectedClass = `${optionName}-option-selected`;
        
        // Find the checked input (if any)
        let checkedInput = null;
        inputs.forEach(input => {
            if (input.checked) {
                checkedInput = input;
            }
        });
        
        // Fallback for No icon label if saved label is No icon
        if (!checkedInput && optionName === 'icon' && this.savedCustomizationData && this.savedCustomizationData["icon-label"] === "No icon") {
            inputs.forEach(input => {
                if (input.value === "No icon" || input.dataset.optionLabel === "No icon") {
                    input.checked = true;
                    checkedInput = input;
                }
            });
        }
        
        // Update ALL labels for this option group
        const allLabels = this.querySelectorAll(
            `label[for*="${optionName}"], label[class*="${optionName}"], label:has(input[name="${optionName}"])`
        );
        
        allLabels.forEach(label => {
            // Remove selected class from all
            label.classList.remove(selectedClass);
            const span = label.querySelector('.option-preview__wrapper');
            if (span) span.classList.remove(selectedClass);
            
            // Add to the correct label if we have a checked input
            if (checkedInput) {
                const labelForInput = checkedInput.closest('label') || 
                                    this.querySelector(`label[for="${checkedInput.id}"]`);
                if (label === labelForInput) {
                    label.classList.add(selectedClass);
                    if (span) span.classList.add(selectedClass);
                }
            }
        });
    }

    disconnectedCallback() {
        // Clean up event listeners
        if (this._clickHandlerBound) {
            this.removeEventListener('click', this._clickHandlerBound, true);
        }
        
        // Clear timeouts
        if (this._optionUpdateTimeout) {
            clearTimeout(this._optionUpdateTimeout);
        }
    }

    handleOptionInputChange(e, optionName) {
        const input = e.target;
        const optionData = input.dataset.optionData;
        const optionLabel = input.dataset.optionLabel;
        
        if (input.checked) {
            this.savedCustomizationData[optionName] = optionData;
            this.savedCustomizationData[`${optionName}-label`] = optionLabel;
            this.personalizationProperties[`${optionName}-label`] = optionLabel;
        } else if (optionName !== 'font-style') {
            this.savedCustomizationData[optionName] = "";
            this.savedCustomizationData[`${optionName}-label`] = "";
            this.personalizationProperties[`${optionName}-label`] = "";
        }
        
        if (this.isBulkMode) {
            this.updateCanvasContent();
        } else {
            this.renderAllCanvases();
        }
        
        this.updateNextButtonState();
        this.addUniqueClassesToOptionLabels();
        this.dispatchSavedEvent();
        this.updatePreviewSummary();
    }
    
    handleOptionLabelClick(e, optionName) {
        const label = e.currentTarget;
        
        // Find the input - handle both nested and separate cases
        let input = label.querySelector('input[type="radio"]');
        if (!input && label.hasAttribute('for')) {
            const inputId = label.getAttribute('for');
            input = this.querySelector(`#${inputId}`);
        }
        
        if (!input) {
            console.error(`❌ Could not find input for ${optionName} label:`, label);
            return;
        }
        
        const optionData = input.dataset.optionData;
        const optionLabel = input.dataset.optionLabel || "";
        const allInputs = this.querySelectorAll(`input[name="${optionName}"]`);
        const currentSavedLabel = this.savedCustomizationData[`${optionName}-label`];
        const isCurrentlySelected = !!(currentSavedLabel && currentSavedLabel === optionLabel);
        
        if (isCurrentlySelected) {
            // Font-style is required, can't deselect
            if (optionName === 'font-style') return;
            
            // Font-pattern should not be deselected
            if (optionName === 'font-pattern') return;
            
            // Only icons can be deselected
            if (optionName === 'icon') {
                input.checked = false;
                this.savedCustomizationData[optionName] = "";
                this.savedCustomizationData[`${optionName}-label`] = "";
                this.personalizationProperties[`${optionName}-label`] = "";
            }
        } else {
            // Select this one
            allInputs.forEach(opt => {
                opt.checked = false;
            });
            input.checked = true;
            this.savedCustomizationData[optionName] = optionData || "";
            this.savedCustomizationData[`${optionName}-label`] = optionLabel || "";
            this.personalizationProperties[`${optionName}-label`] = optionLabel || "";
        }
        
        // Handle rendering based on mode
        if (this.isBulkMode) {
            // In bulk mode, immediately render and dispatch event
            this.renderAllCanvases();
            this.dispatchEvent(new CustomEvent('BulkCustomizerChange', {
                detail: this.savedCustomizationData
            }));
        } else {
            // Single mode behavior
            this.updateNextButtonState();
            this.addUniqueClassesToOptionLabels();
            this.renderAllCanvases();
            this.dispatchSavedEvent();
            this.updatePreviewSummary();
        }
    }

    setupAllEvents() {
        if (this.editCustomizationBtn) {
            this.editCustomizationBtn.addEventListener("click", (e) => {
                e.preventDefault();
                this.showCustomizationSteps();
            });
        }
        
        this.setupQuantityEvents();
        
        if (!this.isBulkMode && this.addToCartBtn) {
            this.addToCartBtn.replaceWith(this.addToCartBtn.cloneNode(true));
            this.addToCartBtn = this.querySelector("button.addtocart--button");
            
            this.addToCartBtn.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.handleAddToCart();                
            });
        }

        window.addEventListener('resize', this.debounce(() => {
            this.handleResize();
        }, 250));
    }
    
    handleBulkFontPatternSelection(input) {
        const allInputs = this.querySelectorAll(`input[name="font-pattern"]`);
        const isCurrentlySelected = input.checked;
        
        // Prevent deselection for font-pattern
        if (isCurrentlySelected) {
            // Do nothing - keep it selected
            return;
        } else {
            // Select this one
            allInputs.forEach(opt => {
                opt.checked = false;
            });
            input.checked = true;
            this.savedCustomizationData["font-pattern"] = input.dataset.optionData;
            this.savedCustomizationData["font-pattern-label"] = input.dataset.optionLabel;
            this.personalizationProperties["font-pattern-label"] = input.dataset.optionLabel;
        }
        
        // Force immediate rendering with proper canvas handling
        this.renderAllCanvasesForBulk();
        this.dispatchEvent(new CustomEvent('BulkCustomizerChange', {
            detail: this.savedCustomizationData
        }));
        
        this.addUniqueClassesToOptionLabels();
    }
    
    renderAllCanvasesForBulk() {
        if (!this.isBulkMode) return;
                
        const canvases = [this.mainCanvas, this.canvas, this.canvasFontStyle, this.canvasFontPattern, this.canvasIcon, this.canvasPreview];
        
        canvases.forEach(canvas => {
            if (canvas) {
                // Clear the canvas completely
                const ctx = canvas.getContext("2d");
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                
                // Redraw the product image
                if (this.productImg && this.productImg.complete) {
                    this.processCanvasImage(canvas, ctx, this.productImg);
                }
                
                // Redraw the personalization
                this.renderCanvas(this.savedCustomizationData, ctx, canvas);
            }
        });
    }
    
    handleBulkIconSelection(input) { 
        const optionData = input.dataset.optionData;
        const optionLabel = input.dataset.optionLabel || "";
        const allInputs = this.querySelectorAll(`input[name="icon"]`);
        const currentSavedLabel = this.savedCustomizationData ? this.savedCustomizationData["icon-label"] : "";
        const isCurrentlySelected = !!(currentSavedLabel && currentSavedLabel === optionLabel);
        
        if (isCurrentlySelected) {
            // Deselect in bulk mode
            input.checked = false;
            this.savedCustomizationData["icon"] = "";
            this.savedCustomizationData["icon-label"] = "";
            this.personalizationProperties["icon-label"] = "";
            // Icon deselected: restore vertical orientation button visibility
            this.updateBulkIconOrientationExclusion(false);
        } else {
            // Select this one
            allInputs.forEach(opt => {
                opt.checked = false;
            });
            input.checked = true;
            const hasActualIcon = !!(optionData && optionData.trim() !== "");
            this.savedCustomizationData["icon"] = optionData || "";
            this.savedCustomizationData["icon-label"] = optionLabel || "";
            this.personalizationProperties["icon-label"] = optionLabel || "";
            // Icon selected: force horizontal orientation and hide vertical button
            this.updateBulkIconOrientationExclusion(hasActualIcon);
        }
        
        // Force immediate rendering with proper canvas handling
        this.renderAllCanvasesForBulk();
        this.dispatchEvent(new CustomEvent('BulkCustomizerChange', {
            detail: this.savedCustomizationData
        }));
        
        this.addUniqueClassesToOptionLabels();
    }

    /**
     * Enforces mutual exclusion between icon selection and vertical text orientation (bulk mode).
     * @param {boolean} iconSelected - true when an icon has just been selected
     */
    updateBulkIconOrientationExclusion(iconSelected) {
        const orientationButtons = this.querySelectorAll('.text-orientation__option');
        if (iconSelected) {
            // Hide vertical orientation button and force horizontal
            orientationButtons.forEach(btn => {
                if (btn.dataset.role === 'vertical') {
                    btn.style.display = 'none';
                }
                if (btn.dataset.role === 'horizontal') {
                    btn.classList.add('active');
                    btn.style.display = '';
                }
            });
            // Reset orientation data to horizontal
            this.savedCustomizationData['text-orientation'] = 'horizontal';
            if (this.personalizationProperties) {
                this.personalizationProperties['text-orientation'] = 'horizontal';
            }
        } else {
            // Icon deselected: restore vertical orientation button visibility
            orientationButtons.forEach(btn => {
                btn.style.display = '';
            });
        }
    }
    
    handleBulkFontStyleSelection(input) {
        // Use the same logic as single mode
        const allInputs = this.querySelectorAll(`input[name="font-style"]`);
        
        // Font style is required, so we don't allow deselection
        allInputs.forEach(opt => {
            opt.checked = false;
        });
        input.checked = true;
        this.savedCustomizationData["font-style"] = input.dataset.optionData;
        this.savedCustomizationData["font-style-label"] = input.dataset.optionLabel;
        this.personalizationProperties["font-style-label"] = input.dataset.optionLabel;
        
        // In bulk mode, force immediate rendering
        if (this.isBulkMode) {
            this.renderAllCanvases();
            this.dispatchEvent(new CustomEvent('BulkCustomizerChange', {
                detail: this.savedCustomizationData
            }));
        } else {
            // Single mode behavior
            this.updateNextButtonState();
            this.addUniqueClassesToOptionLabels();
            this.renderAllCanvases();
            this.dispatchSavedEvent();
            this.updatePreviewSummary();
        }
    }
    
    handleBulkTextUpdate(text) {
        if (!this.isBulkMode) return;
        
        this.savedCustomizationData["personalized-text"] = text;
        this.hasText = text.trim().length > 0;
        
        this.updateCanvasContent();
        this.updateNextButtonState();
    }
    
    handleBulkTextChange(text) {
        if (!this.isBulkMode) return;       
        
        this.savedCustomizationData["personalized-text"] = text;
        this.hasText = text && text.trim().length > 0;
        
        if (this.textInput) {
            this.textInput.value = text;
        }
        
        // Use the new bulk rendering method
        this.renderAllCanvasesForBulk();
        this.updateNextButtonState();
    }

    setupQuantityEvents() {
        this.addEventListener('click', (e) => {
            const minusBtn = e.target.closest('[data-role="minus"]');
            const plusBtn = e.target.closest('[data-role="plus"]');
            
            if (minusBtn) {
                e.preventDefault();
                this.handleQuantityChange(-1);
            }
            
            if (plusBtn) {
                e.preventDefault();
                this.handleQuantityChange(1);
            }
        });

        this.addEventListener('input', (e) => {
            if (e.target.classList.contains('quantity-input')) {
                this.handleQuantityInput(e);
            }
        });

        this.addEventListener('change', (e) => {
            if (e.target.classList.contains('quantity-input')) {
                this.handleQuantityInput(e);
            }
        });
    }

    handleQuantityChange(change) {
        const maxQuantity = this.getMaxQuantity();
        let newValue = this.currentQuantity + change;
        
        if (newValue < 1) newValue = 1;
        if (newValue > maxQuantity) newValue = maxQuantity; 
        
        this.currentQuantity = newValue;
        this.updateAllQuantityInputs(newValue);
    }
    
    handleQuantityInput(e) {
        const maxQuantity = this.getMaxQuantity();
        let value = parseInt(e.target.value) || 1;
        
        if (value < 1) value = 1;
        if (value > maxQuantity) value = maxQuantity; 
        
        this.currentQuantity = value;
        this.updateAllQuantityInputs(value);
    }
    
    updateAllQuantityInputs(value) {
        const maxQuantity = this.getMaxQuantity();
        const allQuantityInputs = this.querySelectorAll('.quantity-input');
        const plusButtons = this.querySelectorAll('[data-role="plus"]');
        
        allQuantityInputs.forEach(input => {
            input.value = value;
        });
        
        plusButtons.forEach(button => {
            if (value >= maxQuantity) {
                button.disabled = true;
                button.style.opacity = '0.5';
                button.style.cursor = 'not-allowed';
            } else {
                button.disabled = false;
                button.style.opacity = '';
                button.style.cursor = '';
            }
        });
        
        const minusButtons = this.querySelectorAll('[data-role="minus"]');
        minusButtons.forEach(button => {
            if (value <= 1) {
                button.disabled = true;
                button.style.opacity = '0.5';
                button.style.cursor = 'not-allowed';
            } else {
                button.disabled = false;
                button.style.opacity = '';
                button.style.cursor = '';
            }
        });
    }
    
    getCurrentQuantity() {
        return this.currentQuantity;
    }

    resetAllSelections() {                
        if (this.textInput) {
            this.textInput.value = '';
            this.textInput.classList.remove("red-outline");
        }
        
        if (this.textOrientations.length > 0) {
            this.textOrientations.forEach(btn => {
                btn.classList.remove("active");
                // Restore visibility of all orientation buttons on reset
                btn.style.display = '';
            });
            const defaultOrientation = this.textOrientations[0];
            if (defaultOrientation) {
                defaultOrientation.classList.add("active");
            }
        }
        
        this.resetOptionGroup('font-style');
        this.resetOptionGroup('font-pattern');
        this.resetOptionGroup('icon');
        
        // Restore icon section visibility on reset
        this.toggleIconSectionVisibility(true);
        
        this.resetDataObjects();
        
        this.currentQuantity = 1;
        this.updateAllQuantityInputs(1);
        
        this.clearAllCanvases();        
        this.hasText = false;
        this.updateNextButtonState();
        setTimeout(() => {
            this.updateNextButtonState();
        }, 100);
        
        this.showCustomizationStepsAfterReset();
    }

    
    showCustomizationStepsAfterReset() {        
        const previewContainer = this.querySelector('.preview-step-container');
        const customizationContainer = this.querySelector('.customization-steps-container');
        const staticImageContainer = this.querySelector('.static-image-container');       
        
        if (previewContainer) {
            previewContainer.classList.add('hidden-block');
            previewContainer.classList.remove('active');
        }
        
        if (customizationContainer) {
            customizationContainer.classList.remove('hidden-block');
        }
        
        if (staticImageContainer && this.isDesktop) {
            staticImageContainer.classList.remove('hidden-block');
        }
        
        if (!this.isDesktop) {
            this.allSteps.forEach(stepEl => {
                stepEl.classList.remove("active");
            });
            const textStep = this.querySelector(`[data-step="text"]`);
            if (textStep) {
                textStep.classList.add("active");
                this.renderCanvasForStep('text');
            }
        }
        
        this.currentStep = 'text';
        this.updateTabActiveState('text');
        this.updateNextButtonState();
        
        if (this.isDesktop) {
            this.renderMainCanvas();
        } else {
            this.renderCanvasForStep('text');
        }
    }

    resetOptionGroup(optionName) {
        const options = this.querySelectorAll(`input[name="${optionName}"]`);
        
        options.forEach(option => {
            option.checked = false;
        });
        
        if (optionName === 'font-style' && options.length > 0) {
            options[0].checked = true;
            
            Object.assign(this.savedCustomizationData, {
                [optionName]: options[0].dataset.optionData || "",
                [`${optionName}-label`]: options[0].dataset.optionLabel || ""
            });
    
            Object.assign(this.personalizationProperties, {
                [`${optionName}-label`]: options[0].dataset.optionLabel || ""
            });
        } else {
            Object.assign(this.savedCustomizationData, {
                [optionName]: "",
                [`${optionName}-label`]: ""
            });
    
            Object.assign(this.personalizationProperties, {
                [`${optionName}-label`]: ""
            });
        }
        
        this.addUniqueClassesToOptionLabels();
        
        this.renderAllCanvases();
        this.dispatchSavedEvent();
    }

    resetDataObjects() {
        const defaultOrientationButton = Array.from(this.textOrientations).find(btn => btn.classList.contains("active"));
        const defaultFontStyleOption = this.fontStyleOptions?.[0];
        
        this.personalizationProperties = {
            "personalized-text": "",
            "text-orientation": `${defaultOrientationButton?.dataset?.role || 'horizontal'}`,
            "font-style-label": `${defaultFontStyleOption?.dataset.optionLabel || ''}`,
            "font-pattern-label": "",
            "icon-label": ""
        };

        this.savedCustomizationData = {
            "personalized-text": "",
            "text-orientation": `${defaultOrientationButton?.dataset?.role || 'horizontal'}`,
            "font-style": `${defaultFontStyleOption?.dataset.optionData || ''}`,
            "font-style-label": `${defaultFontStyleOption?.dataset.optionLabel || ''}`,
            "font-pattern": "",
            "font-pattern-label": "",
            "icon": "",
            "icon-label": "",
        };
    }
    

    resetStepNavigation() {
        this.currentStep = 'text';       
        this.showCustomizationSteps();
        
        if (this.isDesktop && this.scrollContainer) {
            this.scrollContainer.scrollTop = 0;
        }
    }

    clearAllCanvases() {
        const canvases = [
            this.mainCanvas, 
            this.canvas, 
            this.canvasFontStyle, 
            this.canvasFontPattern, 
            this.canvasIcon, 
            this.canvasPreview
        ];
        
        canvases.forEach(canvas => {
            if (canvas) {
                const ctx = canvas.getContext("2d");
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                
                if (this.productImg && this.productImg.complete) {
                    this.processCanvasImage(canvas, ctx, this.productImg);
                }
            }
        });
    }

    reset() {
        this.resetAllSelections();
    }

    deselectFontPattern() {
        return this.resetOptionGroup('font-pattern');
    }

    deselectIcon() {
        return this.resetOptionGroup('icon');
    }

    deselectFontStyle() {
        return false;
    }

    setupStepNavigation() {
        if (this.isBulkMode) {
            return;
        }
        
        if (this.desktopNextButton) {
            const newDesktopButton = this.desktopNextButton.cloneNode(true);
            this.desktopNextButton.parentNode.replaceChild(newDesktopButton, this.desktopNextButton);
            this.desktopNextButton = newDesktopButton;
            
            this.desktopNextButton.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.goToNextStep();
            });
        }
    
        const mobileNextButtons = this.querySelectorAll(".mobile-step-navigation .step-next");
        mobileNextButtons.forEach((button, index) => {
            const newButton = button.cloneNode(true);
            button.parentNode.replaceChild(newButton, button);
        });
    
        this.mobileNextButtons = this.querySelectorAll(".mobile-step-navigation .step-next");
        
        this.mobileNextButtons.forEach((button) => {
            button.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.goToNextStep();
            });
        });
    }

    setupMobileTabs() {
        if (this.isBulkMode) {
            return;
        }
        
        if (!this.allTabButtons || this.allTabButtons.length === 0) {
            console.warn('⚠️ No mobile tab buttons found');
            return;
        }
        
        this.allTabButtons.forEach(tab => {
            const newTab = tab.cloneNode(true);
            tab.parentNode.replaceChild(newTab, tab);
        });
        
        this.allTabButtons = this.querySelectorAll('.tab-button');
        
        this.allTabButtons.forEach(tab => {
            tab.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleTabClick(e);
            });
        });
        
        this.updateTabActiveState(this.currentStep);
    }
    
    lockButtonState() {
        const hasText = this.hasText;
        
        if (!hasText) {
            if (this.desktopNextButton) {
                this.desktopNextButton.disabled = true;
                this.desktopNextButton.setAttribute('disabled', 'disabled');
                this.desktopNextButton.style.opacity = '0.5';
                this.desktopNextButton.style.cursor = 'not-allowed';
                this.desktopNextButton.style.pointerEvents = 'none';
            }
            
            this.mobileNextButtons.forEach(button => {
                button.disabled = true;
                button.setAttribute('disabled', 'disabled');
                button.style.opacity = '0.5';
                button.style.cursor = 'not-allowed';
                button.style.pointerEvents = 'none';
            });
        }
    }
    
    updateTextStateAttribute() {
        const hasText = this.hasText;
        const hasIcon = this.savedCustomizationData["icon"] && this.savedCustomizationData["icon"].trim() !== "";
        const hasTextOrIcon = hasText || hasIcon;
        
        if (hasTextOrIcon) {
            this.setAttribute('data-has-text', 'true');
        } else {
            this.setAttribute('data-has-text', 'false');
        }
    }

    handleTextInput(e) {
        this.updateTextStateAttribute();
        this.textInput.classList.remove("red-outline");

        let text = e.target.value;
        
        if (text.length > 0) {
            text = text.replace(/[^a-zA-Z ]/g, "");
            text = text.replace(/^\s+/, "");
            text = text.slice(0, MAX_TEXT_LENGTH);
            
            if (text !== e.target.value) {
                e.target.value = text;
            }
        }

        const trimmedText = text.trim();
        
        this.hasText = trimmedText.length > 0;
        
        this.savedCustomizationData["personalized-text"] = trimmedText;
        this.personalizationProperties["personalized-text"] = trimmedText;
        
        if (this.isBulkMode) {
            this.updateCanvasContent();
        } else {
            this.renderAllCanvases();
        }
        
        this.updateNextButtonState();
        this.lockButtonState();
        this.dispatchSavedEvent();
    }

    handleOrientationChange(btn) {
        if (this.hasText || this.savedCustomizationData["icon"]) {
            this.textOrientations.forEach(btn => btn.classList.remove("active"));
            btn.classList.add("active");
            this.savedCustomizationData["text-orientation"] = btn.dataset.role;
            this.personalizationProperties["text-orientation"] = btn.dataset.role;

            // When vertical orientation is chosen, clear icon selection and hide icon section
            if (btn.dataset.role === 'vertical') {
                // Clear icon selection
                const iconInputs = this.querySelectorAll('input[name="icon"]');
                iconInputs.forEach(iconInput => { iconInput.checked = false; });
                this.savedCustomizationData['icon'] = '';
                this.savedCustomizationData['icon-label'] = '';
                if (this.personalizationProperties) {
                    this.personalizationProperties['icon-label'] = '';
                }
                this.updateOptionClasses('icon');
                // Hide icon step and icon tab buttons
                this.toggleIconSectionVisibility(false);
            } else {
                // Horizontal selected: restore icon section visibility
                this.toggleIconSectionVisibility(true);
            }

            this.renderAllCanvases();
            this.dispatchSavedEvent();
        } else {
            this.textInput.classList.add("red-outline");
        }
    }

    /**
     * Shows or hides the icon personalization step and its tab buttons (single mode).
     * @param {boolean} visible
     */
    toggleIconSectionVisibility(visible) {
        const iconStep = this.querySelector('.personalization-step[data-step="icon"]');
        if (iconStep) {
            iconStep.style.display = visible ? '' : 'none';
        }
        // Hide/show icon tab buttons across all mobile tab nav bars
        const iconTabButtons = this.querySelectorAll('.tab-button[data-tab="icon"]');
        iconTabButtons.forEach(tabBtn => {
            tabBtn.style.display = visible ? '' : 'none';
        });
    }

    handleVariantChange(variant_id) {
        this.variantId = variant_id;

        // Add this line to update quantity limits
         this.updateQuantityLimits(variant_id);
        
        const newImageUrl = this.variantsPersonalizationImages[this.variantId];
        if (this.productImg.src !== newImageUrl) {
            this.productImg = new Image();
            this.productImg.src = newImageUrl;
            this.productImg.onload = () => {
                this.customizationRegionData = this.convertRegionDataToCanvas(this.regionScriptData, this.mainCanvas || this.canvas);
                this.renderAllCanvases();
            };
        } else {
            this.customizationRegionData = this.convertRegionDataToCanvas(this.regionScriptData, this.mainCanvas || this.canvas);
            this.renderAllCanvases();
        }
    }

    updateNextButtonState() {
        let shouldDisable = false;
        let buttonText = 'Next';
        
        const hasText = this.hasText;
        const hasIcon = this.savedCustomizationData["icon"] && this.savedCustomizationData["icon"].trim() !== "";        
        shouldDisable = !hasText;       
        if (this.desktopNextButton) {            
            this.desktopNextButton.disabled = shouldDisable;
            
            if (shouldDisable) {
                this.desktopNextButton.setAttribute('disabled', 'disabled');
                this.desktopNextButton.classList.add('disabled');
                this.desktopNextButton.style.opacity = '0.5';
                this.desktopNextButton.style.cursor = 'not-allowed';
                this.desktopNextButton.style.pointerEvents = 'none';
            } else {
                this.desktopNextButton.removeAttribute('disabled');
                this.desktopNextButton.classList.remove('disabled');
                this.desktopNextButton.style.opacity = '';
                this.desktopNextButton.style.cursor = '';
                this.desktopNextButton.style.pointerEvents = '';
            }
            
            this.desktopNextButton.textContent = buttonText;
        } 
        
        this.mobileNextButtons.forEach((button, index) => {
            button.disabled = shouldDisable;
            if (shouldDisable) {
                button.setAttribute('disabled', 'disabled');
                button.style.opacity = '0.5';
                button.style.cursor = 'not-allowed';
                button.style.pointerEvents = 'none';
            } else {
                button.removeAttribute('disabled');
                button.style.opacity = '';
                button.style.cursor = '';
                button.style.pointerEvents = '';
            }
            button.textContent = buttonText;
        });
    }
    
    forceButtonTest() {
        this.hasText = false;
        this.savedCustomizationData["personalized-text"] = "";
        this.savedCustomizationData["icon"] = "";
        this.updateNextButtonState();        
    }

    
    handleTabClick(e) {
        const button = e.currentTarget;
        const targetStep = button.getAttribute('data-tab');
        
        if (targetStep && targetStep !== this.currentStep) {
            this.goToStep(targetStep);
        }
    }

    updateTabActiveState(activeStep) {
        if (!this.allTabButtons || this.allTabButtons.length === 0) return;
        
        this.allTabButtons.forEach(tab => {
            const tabStep = tab.getAttribute('data-tab');
            const isActive = tabStep === activeStep;
            
            if (isActive) {
                tab.classList.add("active");
                tab.setAttribute('aria-selected', 'true');
            } else {
                tab.classList.remove("active");
                tab.setAttribute('aria-selected', 'false');
            }
        });
    }

    goToStep(step) {    
        if (this.isBulkMode) {
            return;
        }
        
        const previewContainer = this.querySelector('.preview-step-container');
        
        if (!step || !this.availableSteps.includes(step)) {
            console.error('❌ Step not available:', step);
            return;
        }
    
        this.currentStep = step;
    
        if (this.isDesktop) {
            this.scrollToStep(step);
        } else {
            this.allSteps.forEach(stepEl => {
                stepEl.classList.remove("active");
            });
            
            if (previewContainer) {
                previewContainer.classList.remove("active");
            }
    
            const targetStepEl = this.querySelector(`[data-step="${step}"]`);
            if (targetStepEl) {
                targetStepEl.classList.add("active");
                this.renderCanvasForStep(step);
            }
    
            this.updateTabActiveState(step);
        }
    
        this.updateNextButtonState();
        
        if (!this.isDesktop) {
            this.renderCanvasForStep(step);
        }
    }

    scrollToStep(step) {
        if (!this.scrollContainer || !this.isDesktop) return;

        const targetStepEl = this.querySelector(`[data-step="${step}"]`);
        if (targetStepEl) {
            this.isProgrammaticScroll = true;

            const targetPosition = targetStepEl.offsetTop - this.scrollContainer.offsetTop;

            this.scrollContainer.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
            });

            setTimeout(() => {
                this.isProgrammaticScroll = false;
                this.renderMainCanvas();
            }, 500);
        }
    }

    goToNextStep() {
        if (this.isProcessingNext) return;
        
        this.isProcessingNext = true;      
        
        try {
            if (this.isDesktop) {
                this.handleDesktopNextStep();
            } else {
                this.handleMobileNextStep();
            }
        } catch (error) {
            console.error('❌ Error in goToNextStep:', error);
            this.showPreview();
            this.isProcessingNext = false;
        }
    }
    
    handleMobileNextStep() {
        const currentIndex = this.availableSteps.indexOf(this.currentStep);
        
        if (currentIndex === this.availableSteps.length - 2) {
            this.showPreview();
            this.isProcessingNext = false;
            return;
        }
        
        const nextStepIndex = currentIndex + 1;
        const nextStep = this.availableSteps[nextStepIndex];
        
        if (nextStep && nextStep !== 'preview') {
            this.currentStep = nextStep;
            this.goToStep(nextStep);
        } else {
            this.showPreview();
        }
        
        this.isProcessingNext = false;
    }
    updateQuantityLimits(variantId) {
        const variantInventory = this.allVariantsInventory ? this.allVariantsInventory[variantId] : null;
        const maxQuantity = variantInventory ? variantInventory.quantity : 999; // Fallback
        
        // Update all quantity inputs
        this.querySelectorAll('.quantity-input').forEach(input => {
          input.setAttribute('max', maxQuantity);
          
          // Adjust current value if it exceeds the new max
          const currentValue = parseInt(input.value);
          if (currentValue > maxQuantity) {
            input.value = maxQuantity;
            this.currentQuantity = maxQuantity;
          }
        });
        
        // Update plus/minus button states
        this.updateAllQuantityInputs(this.currentQuantity);
      }
    
    handleDesktopNextStep() {
        const currentIndex = this.availableSteps.indexOf(this.currentStep);
       
        if (currentIndex === this.availableSteps.length - 2) {
            this.showPreview();
            this.isProcessingNext = false;
            return;
        }
        
        let nextStepIndex = currentIndex + 1;
        let nextStep = this.availableSteps[nextStepIndex];
        
        if (this.isDesktop && nextStep === 'font-style') {
            const hasFontStyleSelected = this.savedCustomizationData["font-style"] && 
                                        this.savedCustomizationData["font-style"].trim() !== "";
            const hasCustomization = this.hasText || this.savedCustomizationData["icon"];
            
            if (hasFontStyleSelected && hasCustomization) {
                nextStepIndex = currentIndex + 2;
                nextStep = this.availableSteps[nextStepIndex];
                
                if (!nextStep || nextStep === 'preview') {
                    this.showPreview();
                    this.isProcessingNext = false;
                    return;
                }
            }
        }
        
        if (nextStep && nextStep !== 'preview') {
            this.currentStep = nextStep;
            this.goToStep(nextStep);
        } else {
            this.showPreview();
        }
        
        this.isProcessingNext = false;
    }

    setupScrollEvents() {
        if (!this.isDesktop || !this.scrollContainer) return;
        
        this.scrollContainer.addEventListener('scroll', this.debounce(() => {
            if (this.isProgrammaticScroll) return;
            
            if (!this.hasText) {
                return;
            }
            
            this.detectCurrentStepFromScroll();
        }, 100)); 
    }

    debugScrollContainer() {
        if (!this.isDesktop || !this.scrollContainer) return;       
    }
    
    detectCurrentStepFromScroll() {
        if (!this.isDesktop || !this.scrollContainer) return;
        
        const scrollTop = this.scrollContainer.scrollTop;
        const scrollContainerHeight = this.scrollContainer.clientHeight;
        const scrollBottom = scrollTop + scrollContainerHeight;
        
        let activeStep = this.currentStep;
        let maxVisibility = 0;
        
        this.availableSteps.forEach(step => {
            if (step === 'preview') return;
            
            const stepElement = this.querySelector(`[data-step="${step}"]`);
            if (stepElement) {
                const stepTop = stepElement.offsetTop - this.scrollContainer.offsetTop;
                const stepBottom = stepTop + stepElement.offsetHeight;
                const stepHeight = stepElement.offsetHeight;
                
                const visibleTop = Math.max(stepTop, scrollTop);
                const visibleBottom = Math.min(stepBottom, scrollBottom);
                const visibleHeight = Math.max(0, visibleBottom - visibleTop);
                
                const visibilityPercentage = (visibleHeight / stepHeight) * 100;
                
                if (visibilityPercentage > maxVisibility && visibilityPercentage > 30) {
                    maxVisibility = visibilityPercentage;
                    activeStep = step;
                }
            }
        });
        
        const containerScrollHeight = this.scrollContainer.scrollHeight;
        const isAtBottom = scrollBottom >= containerScrollHeight - 50;
        if (isAtBottom && maxVisibility < 30) {            
            return;
        }
         if (activeStep !== this.currentStep && maxVisibility > 30) {            
            this.currentStep = activeStep;
            this.updateNextButtonState();
        }
    }

    validateStepConfiguration() {
        const existingSteps = [];
        this.availableSteps.forEach(step => {
            const element = this.querySelector(`[data-step="${step}"]`);
            if (element || step === 'preview') {
                existingSteps.push(step);
            } else {
                console.log(`❌ Step "${step}" not found in DOM`);
            }
        });

        this.availableSteps = existingSteps;
    }

    debugStepTransitions() {
        const currentIndex = this.availableSteps.indexOf(this.currentStep);
        const isLastStep = currentIndex === this.availableSteps.length - 2;
    }

    getNextIncompleteStep() {
        const hasTextOrIcon = this.hasText || this.savedCustomizationData["icon"];
        
        if (!hasTextOrIcon) {
            return 'text';
        }
        
        for (let i = 0; i < this.availableSteps.length; i++) {
            const step = this.availableSteps[i];
            
            switch(step) {
                case 'text':
                    if (hasTextOrIcon) continue;
                    break;
                case 'font-style':
                    if (!this.savedCustomizationData["font-style"]) {
                        return step;
                    }
                    break;
                case 'font-pattern':
                    if (this.availableSteps.includes('font-pattern') && !this.savedCustomizationData["font-pattern"]) {
                        return step;
                    }
                    break;
                case 'icon':
                    if (this.availableSteps.includes('icon') && !this.savedCustomizationData["icon"]) {
                        return step;
                    }
                    break;
            }
        }
        
        return null;
    }
    
    showPreview() {        
        const previewContainer = this.querySelector('.preview-step-container');
        
        if (this.isDesktop) {
            if (this.customizationContainer) {
                this.customizationContainer.classList.add('hidden-block');
            }
            if (this.staticImageContainer) {
                this.staticImageContainer.classList.add('hidden-block');
            }
            if (previewContainer) {
                previewContainer.classList.remove('hidden-block');                
                
                this.updatePreviewSummary();
                this.renderPreviewCanvas();
            }
        } else {
            this.allSteps.forEach(stepEl => {
                stepEl.classList.remove("active");
            });
            
            if (this.customizationContainer) {
                this.customizationContainer.classList.add('hidden-block');
            }
            
            if (previewContainer) {
                previewContainer.classList.add("active");
                previewContainer.classList.remove('hidden-block');
            }
            
            this.renderCanvasForStep('preview');
            this.updatePreviewSummary();
        }
        
        this.isProcessingNext = false;
    }
    
    showCustomizationSteps() {        
        const previewContainer = this.querySelector('.preview-step-container');
        
        if (this.isDesktop) {
            if (this.customizationContainer) {
                this.customizationContainer.classList.remove('hidden-block');
            }
            if (this.staticImageContainer) {
                this.staticImageContainer.classList.remove('hidden-block');
            }
            if (previewContainer) {
                previewContainer.classList.add('hidden-block');
            }
            
            this.currentStep = 'text';
            if (this.scrollContainer) {
                this.scrollContainer.scrollTop = 0;
            }
            this.restoreOptionSelections();
            this.updateNextButtonState();
            
            // Clear and redraw all canvases
            this.clearAllCanvases();
            setTimeout(() => {
                this.renderMainCanvas();
            }, 50);
        } else {
            if (previewContainer) {
                previewContainer.classList.remove("active");
                previewContainer.classList.add('hidden-block');
            }
            
            if (this.customizationContainer) {
                this.customizationContainer.classList.remove('hidden-block');
            }
            this.restoreOptionSelections();            
            this.goToStep('text');
            
            // Clear and redraw canvas for mobile
            setTimeout(() => {
                this.clearAllCanvases();
                this.renderCanvasForStep('text');
            }, 50);
        }
    }

    renderCanvasForStep(step) {
        if (this.isDesktop && !this.isBulkMode) return;
    
        let canvas;
        
        switch(step) {
            case 'text':
                canvas = this.canvas;
                break;
            case 'font-style':
                canvas = this.canvasFontStyle;
                break;
            case 'font-pattern':
                canvas = this.canvasFontPattern;
                break;
            case 'icon':
                canvas = this.canvasIcon;
                break;
            case 'preview':
                canvas = this.canvasPreview;
                break;
        }
    
        if (canvas) {
            if (!this.isBulkMode) {
                const allCanvases = [this.canvas, this.canvasFontStyle, this.canvasFontPattern, this.canvasIcon, this.canvasPreview];
                allCanvases.forEach(c => {
                    if (c) c.style.display = 'none';
                });
                
                canvas.style.display = 'block';
            }
            
            const ctx = canvas.getContext("2d");
            this.renderCanvas(this.savedCustomizationData, ctx, canvas);
        }
    }

    renderMainCanvas() {
        if (!this.isDesktop || !this.mainCanvas) return;
        
        const ctx = this.mainCanvas.getContext("2d");
        this.renderCanvas(this.savedCustomizationData, ctx, this.mainCanvas);
    }

    renderPreviewCanvas() {
        if (!this.canvasPreview) return;

        if (!this.isBulkMode) {
            this.canvasPreview.style.display = 'block';
        }
        
        const ctx = this.canvasPreview.getContext("2d");
        this.renderCanvas(this.savedCustomizationData, ctx, this.canvasPreview);
    }

    restoreOptionSelections() {
        if (this.savedCustomizationData["font-style"] && this.savedCustomizationData["font-style"].trim() !== "") {
            const fontStyleOption = this.querySelector(`input[name="font-style"][data-option-data="${this.savedCustomizationData["font-style"]}"]`);
            if (fontStyleOption) {
                this.fontStyleOptions.forEach(option => option.checked = false);
                fontStyleOption.checked = true;
            } else {
                console.warn('⚠️ Font style option not found:', this.savedCustomizationData["font-style"]);
                if (this.fontStyleOptions.length > 0) {
                    this.fontStyleOptions.forEach(option => option.checked = false);
                    this.fontStyleOptions[0].checked = true;
                }
            }
        } else {
            if (this.fontStyleOptions.length > 0) {
                this.fontStyleOptions.forEach(option => option.checked = false);
                this.fontStyleOptions[0].checked = true;
            }
        }
        
        if (this.savedCustomizationData["font-pattern"] && this.savedCustomizationData["font-pattern"].trim() !== "") {
            const fontPatternOption = this.querySelector(`input[name="font-pattern"][data-option-data="${this.savedCustomizationData["font-pattern"]}"]`);
            if (fontPatternOption) {
                this.fontPatternOptions.forEach(option => option.checked = false);
                fontPatternOption.checked = true;
            }
        } else {
            this.fontPatternOptions.forEach(option => option.checked = false);
        }
        
        if (this.savedCustomizationData["icon"] && this.savedCustomizationData["icon"].trim() !== "") {
            const iconOption = this.querySelector(`input[name="icon"][data-option-data="${this.savedCustomizationData["icon"]}"]`);
            if (iconOption) {
                this.iconOptions.forEach(option => option.checked = false);
                iconOption.checked = true;
            }
        } else if (this.savedCustomizationData["icon-label"] === "No icon") {
            const noIconOption = this.querySelector(`input[name="icon"][value="No icon"], input[name="icon"][data-option-label="No icon"]`);
            if (noIconOption) {
                this.iconOptions.forEach(option => option.checked = false);
                noIconOption.checked = true;
            }
        } else {
            this.iconOptions.forEach(option => option.checked = false);
        }
        
        this.addUniqueClassesToOptionLabels();
        this.renderAllCanvases();
    }
    
    updatePreviewSummary() {
        if (this.previewText) {
            const text = this.savedCustomizationData["personalized-text"] || "";
            this.previewText.textContent = text || "No text";
        }

        // ADD TEXT ORIENTATION TO PREVIEW
        const textOrientationContainer = this.querySelector('.text-orientation-preview');
        const previewTextOrientation = this.querySelector("#previewTextOrientation");
        
        if (previewTextOrientation && textOrientationContainer) {
            const orientation = this.savedCustomizationData["text-orientation"] || "horizontal";
            previewTextOrientation.textContent = orientation === "vertical" ? "Vertical" : "Horizontal";
            textOrientationContainer.style.display = "block"; // or "block" depending on your CSS
        }
    
        if (this.previewFontStyle) {
            this.previewFontStyle.textContent = this.savedCustomizationData["font-style-label"] || "-";
        }
    
        const fontPatternContainer = this.querySelector('.font-pattern-preview');
        if (this.previewFontPattern && fontPatternContainer) {
            const fontPattern = this.savedCustomizationData["font-pattern-label"];
            if (fontPattern && fontPattern.trim() !== "") {
                this.previewFontPattern.textContent = fontPattern;
                fontPatternContainer.style.display = "inline";
            } else {
                fontPatternContainer.style.display = "none";
            }
        }
    
        const iconContainer = this.querySelector('.icon-preview');
        if (this.previewIcon) {
            const iconData = this.savedCustomizationData["icon"];
            const iconLabel = this.savedCustomizationData["icon-label"];
            if (iconData && iconData.trim() !== "" && iconLabel && iconLabel.trim() !== "" && iconLabel !== "No icon") {
                this.previewIcon.textContent = iconLabel;
                if (iconContainer) iconContainer.style.display = "inline";
            } else {
                if (iconContainer) iconContainer.style.display = "none";
            }
        }
    }
    
    handleAddToCart() {
        if (this.closest('bulk-personalization')) {
            return;
        }
    
        const personalizationData = this.getPersonalizationData();
        const quantity = this.getCurrentQuantity();       
    
        const formData = new FormData();
        formData.append('id', this.variantId);
        formData.append('quantity', quantity);
        
        Object.keys(personalizationData).forEach(key => {
            if (personalizationData[key]) {
                formData.append(`properties[${key}]`, personalizationData[key]);
            }
        });
    
        if (this.addToCartBtn) {
            this.addToCartBtn.disabled = true;
        }
    
        fetch('/cart/add.js', {
            method: 'POST',
            body: formData
        })
        .then(response => response.json())
        .then(data => {            
            this.updateCartCount();
            this.showSuccessMessage();
            
            this.resetAllSelections();
            this.closeModalAndOpenCart();
        })
        .catch((error) => {
            console.error('❌ Single Personalization - Error adding to cart:', error);
            this.showErrorMessage();
            
            if (this.addToCartBtn) {
                this.addToCartBtn.disabled = false;
                this.addToCartBtn.textContent = "Add to Cart";
            }
        });
    }

    updateCartCount() {
        fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
                const cartCountElements = document.querySelectorAll('.cart-count-bubble, .cart-count, [data-cart-count]');
                cartCountElements.forEach(element => {
                    if (element.classList.contains('cart-count-bubble')) {
                        const countElement = element.querySelector('.count');
                        if (countElement) {
                            countElement.textContent = cart.item_count;
                        } else {
                            element.textContent = cart.item_count;
                        }
                    } else {
                        element.textContent = cart.item_count;
                    }
                });
                
                if (cartCountElements.length === 0) {
                    const fallbackElements = document.querySelectorAll('*');
                    fallbackElements.forEach(el => {
                        if (el.textContent && el.textContent.includes('cart') && !isNaN(parseInt(el.textContent))) {
                            el.textContent = el.textContent.replace(/\d+/, cart.item_count);
                        }
                    });
                }
            })
            .catch(error => {
                console.warn('⚠️ Could not update cart count:', error);
            });
    }

    showSuccessMessage() {
        const successMsg = document.createElement('div');
        successMsg.textContent = 'Product added to cart!';
        successMsg.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: green;
            color: white;
            padding: 15px;
            border-radius: 5px;
            z-index: 10000;
            font-family: Arial, sans-serif;
            font-size: 14px;
        `;
        document.body.appendChild(successMsg);
        
        setTimeout(() => {
            if (document.body.contains(successMsg)) {
                document.body.removeChild(successMsg);
            }
        }, 3000);
    }

    showErrorMessage() {
        console.error('❌ Failed to add product to cart');
        
        const errorMsg = document.createElement('div');
        errorMsg.textContent = 'Failed to add product to cart. Please try again.';
        errorMsg.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: red;
            color: white;
            padding: 15px;
            border-radius: 5px;
            z-index: 10000;
            font-family: Arial, sans-serif;
            font-size: 14px;
        `;
        document.body.appendChild(errorMsg);
        
        setTimeout(() => {
            if (document.body.contains(errorMsg)) {
                document.body.removeChild(errorMsg);
            }
        }, 3000);
    }

    closeModal() {
        const modal = this.closest('modal-viewer');
        if (modal) {
            const closeBtn = modal.querySelector('modal-close');
            if (closeBtn) {
                closeBtn.click();
            }
        }
    }
    
    closeModalAndOpenCart() {
        const modalContainer = this.closest('personalize-modal-container');
        if (modalContainer) {
            const closeBtn = modalContainer.querySelector('modal-close');
            if (closeBtn) {
                closeBtn.click();
            } else {
                const modalViewer = this.closest('modal-viewer');
                if (modalViewer) {
                    if (modalViewer.close) {
                        modalViewer.close();
                    } else if (modalViewer.hide) {
                        modalViewer.hide();
                    } else {
                        modalViewer.classList.add('hidden');
                    }
                }
            }
        }
        
        setTimeout(() => {
            this.openCartDrawer();
        }, 500);
    }
    
    openCartDrawer() {
        const cartButtons = document.querySelectorAll('[data-action="open-cart"], .cart-button, #cart-icon, .header__icon--cart');
        if (cartButtons.length > 0) {
            cartButtons[0].click();
            return;
        }
        
        const cartDrawerToggles = document.querySelectorAll('[data-drawer-toggle="cart"], [aria-controls="cart-drawer"]');
        if (cartDrawerToggles.length > 0) {
            cartDrawerToggles[0].click();
            return;
        }
        
        const cartDrawer = document.querySelector('cart-drawer, #cart-drawer, [data-cart-drawer]');
        if (cartDrawer) {
            if (cartDrawer.open) {
                cartDrawer.open();
            } else if (cartDrawer.show) {
                cartDrawer.show();
            } else if (cartDrawer.classList) {
                cartDrawer.classList.add('active', 'open');
            }
            
            document.documentElement.dispatchEvent(new CustomEvent('cart:open'));
            return;
        }
        
        this.showSuccessMessage('Product added to cart! View cart to see your items.');
    }
    
    getPersonalizationData() {
        const cleanData = {};
        
        if (this.savedCustomizationData["personalized-text"] && this.savedCustomizationData["personalized-text"].trim() !== "") {
            cleanData["personalized-text"] = this.savedCustomizationData["personalized-text"];
        }
        
        cleanData["text-orientation"] = this.savedCustomizationData["text-orientation"] || "horizontal";
        
        if (this.savedCustomizationData["font-style"] && this.savedCustomizationData["font-style"].trim() !== "") {
            cleanData["font-style"] = this.savedCustomizationData["font-style"];
        }
        
        if (this.savedCustomizationData["font-pattern-label"] && this.savedCustomizationData["font-pattern-label"].trim() !== "") {
            cleanData["font"] = this.savedCustomizationData["font-pattern-label"];
        }
        
        if (this.savedCustomizationData["icon"] && this.savedCustomizationData["icon"].trim() !== "" && this.savedCustomizationData["icon-label"] && this.savedCustomizationData["icon-label"].trim() !== "" && this.savedCustomizationData["icon-label"] !== "No icon") {
            cleanData["icon"] = this.savedCustomizationData["icon-label"]; // ← Sends the label
        }
        
        return cleanData;
    }

    handleResize() {
        const wasDesktop = this.isDesktop;
        this.isDesktop = window.innerWidth > 768;
        
        if (wasDesktop !== this.isDesktop) {
            if (this.isDesktop) {
                this.showCustomizationSteps();
            }
        }
        
        this.setupAllCanvases();
        this.renderAllCanvases();
    }

    renderAllCanvases() {
        if (this.isBulkMode) {
            const canvases = [this.mainCanvas, this.canvas, this.canvasFontStyle, this.canvasFontPattern, this.canvasIcon, this.canvasPreview];
            canvases.forEach(canvas => {
                if (canvas) {
                    const ctx = canvas.getContext("2d");
                    this.renderCanvas(this.savedCustomizationData, ctx, canvas);
                }
            });
            return;
        }
        
        if (this.isDesktop) {
            this.renderMainCanvas();
        } else {
            this.renderCanvasForStep(this.currentStep);
        }
    }
    
    setCanvasVisibility(stepType) {
        if (!this.isBulkMode) return;
        const allCanvases = [this.mainCanvas, this.canvas, this.canvasFontStyle, this.canvasFontPattern, this.canvasIcon, this.canvasPreview];
        allCanvases.forEach(canvas => {
            if (canvas) canvas.style.display = 'none';
        });
        
        let activeCanvas = null;
        switch(stepType) {
            case 'text':
                activeCanvas = this.canvas;
                break;
            case 'font-style':
                activeCanvas = this.canvasFontStyle;
                break;
            case 'font-pattern':
                activeCanvas = this.canvasFontPattern;
                break;
            case 'icon':
                activeCanvas = this.canvasIcon;
                break;
            default:
                activeCanvas = this.mainCanvas;
        }
        
        if (activeCanvas) {
            activeCanvas.style.display = 'block';
            const ctx = activeCanvas.getContext("2d");
            this.renderCanvas(this.savedCustomizationData, ctx, activeCanvas);
        }
        if (window.innerWidth > 768 && this.mainCanvas && this.mainCanvas !== activeCanvas) {
            this.mainCanvas.style.display = 'block';
            const ctx = this.mainCanvas.getContext("2d");
            this.renderCanvas(this.savedCustomizationData, ctx, this.mainCanvas);
        }
    }
    
    updateCanvasContent() {
        if (!this.isBulkMode) return;
                
        // Use the new bulk rendering method
        this.renderAllCanvasesForBulk();
    }

    dispatchSavedEvent() {
        const targetElement = this.closest("single-personalization");
        if (targetElement) {
            targetElement.dispatchEvent(new Event("SingleCustomizerChange"));
        }
    }

    convertRegionDataToCanvas(customizationRegionData, canvas) {
        const convertedData = {};

        for (const variantID in customizationRegionData) {
            const region = customizationRegionData[variantID];
            if (region.x && region.y && region.width && region.height) {
                convertedData[variantID] = {
                    x: (parseFloat(region.x) / 100) * canvas.width,
                    y: (parseFloat(region.y) / 100) * canvas.height,
                    width: (parseFloat(region.width) / 100) * canvas.width,
                    height: (parseFloat(region.height) / 100) * canvas.height
                };
            } else {
                convertedData[variantID] = region;
            }
        }
        return convertedData;
    }
    
    getMaxQuantity() {
        const firstInput = this.querySelector('.quantity-input');
        if (firstInput && firstInput.hasAttribute('max')) {
            return parseInt(firstInput.getAttribute('max')) || 999; 
        }
        return 999; 
    }
    
    processCanvasImage(canvas, ctx, productImage) {
        const aspectRatio = productImage.width / productImage.height;
        const canvasAspectRatio = canvas.width / canvas.height;
        let drawWidth, drawHeight, offsetX, offsetY;

        if (aspectRatio > canvasAspectRatio) {
            drawWidth = canvas.width;
            drawHeight = drawWidth / aspectRatio;
            offsetX = 0;
            offsetY = (canvas.height - drawHeight) / 2;
        } else {
            drawHeight = canvas.height;
            drawWidth = drawHeight * aspectRatio;
            offsetX = (canvas.width - drawWidth) / 2;
            offsetY = 0;
        }

        ctx.drawImage(productImage, offsetX, offsetY, drawWidth, drawHeight);
    }

    renderCanvas(savedCustomizationData, ctx, canvas) {
        if (!canvas || !ctx || !this.productImg) return;
    
        // COMPLETELY CLEAR THE CANVAS FIRST
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Draw the product image fresh
        this.processCanvasImage(canvas, ctx, this.productImg);
    
        const customizationRegion = this.customizationRegionData[this.variantId];
        if (!customizationRegion) {
            console.error('❌ No customization region found for variant:', this.variantId);
            return;
        }
    
        let text = savedCustomizationData["personalized-text"];
        let textOrientation = savedCustomizationData["text-orientation"] || "horizontal";
        let fontStyle = savedCustomizationData["font-style"] || '';
        let fontPattern = savedCustomizationData["font-pattern"];
        let iconUrl = savedCustomizationData["icon"];
    
        // If no text AND no icon, just show the product image
        if (!text && !iconUrl) {
            return;
        }
    
        let textSize = 190;
        let iconHeight = 0;
        let padding = customizationRegion.height * 0.05;
    
        const convertIconToWhite = (icon, targetWidth, targetHeight) => {
            const tempCanvas = document.createElement("canvas");
            const tempCtx = tempCanvas.getContext("2d");
            tempCanvas.width = targetWidth;
            tempCanvas.height = targetHeight;
    
            tempCtx.drawImage(icon, 0, 0, targetWidth, targetHeight);
            let imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
            let data = imageData.data;
    
            for (let i = 0; i < data.length; i += 4) {
                if (data[i + 3] > 0) {
                    data[i] = 255;
                    data[i + 1] = 255;
                    data[i + 2] = 255;
                }
            }
    
            tempCtx.putImageData(imageData, 0, 0);
            return tempCanvas;
        };
        
        const drawIcon = () => {
            return new Promise((resolve) => {
                if (iconUrl) {
                    const icon = new Image();
                    icon.crossOrigin = "anonymous";
                    icon.onload = () => {
                        // Increase bounding region for larger icon display while preserving natural aspect ratio
                        const hasText = text && text.trim() !== "";
                        const maxW = customizationRegion.width * 0.8;
                        const maxH = customizationRegion.height * (hasText ? 0.55 : 0.75);
                        const aspectRatio = icon.naturalHeight / icon.naturalWidth;
                        
                        let iconDrawWidth = maxW;
                        let iconDrawHeight = iconDrawWidth * aspectRatio;
                        
                        if (iconDrawHeight > maxH) {
                            iconDrawHeight = maxH;
                            iconDrawWidth = iconDrawHeight / aspectRatio;
                        }
                        
                        const iconX = customizationRegion.x + (customizationRegion.width - iconDrawWidth) / 2;
                        
                        let iconY;
                        if (textOrientation === "vertical") {
                            iconY = customizationRegion.y + customizationRegion.height * 0.15;
                        } else {
                            iconY = customizationRegion.y;
                        }
                        
                        ctx.save();
                        const whiteIcon = convertIconToWhite(icon, iconDrawWidth, iconDrawHeight);
                        ctx.drawImage(whiteIcon, iconX, iconY, iconDrawWidth, iconDrawHeight);
                        ctx.restore();
                        
                        if (textOrientation === "vertical") {
                            iconHeight = iconDrawHeight + customizationRegion.height * 0.05;
                        } else {
                            iconHeight = iconDrawHeight + customizationRegion.height * 0.05;
                        }
                        resolve();
                    };
                    icon.onerror = () => {
                        console.error('❌ Failed to load icon:', iconUrl);
                        iconHeight = padding;
                        resolve();
                    };
                    icon.src = iconUrl;
                } else {
                    iconHeight = padding;
                    resolve();
                }
            });
        };
    
        const drawText = () => {
            if (!text) return;
            
            // Save context state at the start
            ctx.save();
        
            let fontWeight = textOrientation === "vertical" ? "bold" : "normal";
            let fontFamily = fontStyle || 'Arial, sans-serif';
                            
            ctx.font = `${fontWeight} ${textSize}px ${fontFamily}`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
        
            let textWidth = ctx.measureText(text).width;
            let textHeight = textSize;
        
            if (textOrientation === "vertical") {
                const availableHeight = customizationRegion.height * (iconUrl ? 0.6 : 0.8);
                if (textWidth > availableHeight) {
                    textSize *= availableHeight / textWidth;
                    ctx.font = `${fontWeight} ${textSize}px ${fontFamily}`;
                    textWidth = ctx.measureText(text).width;
                    textHeight = textSize;
                }
            } else {
                if (textWidth > customizationRegion.width * 0.9) {
                    textSize *= (customizationRegion.width * 0.9) / textWidth;
                    ctx.font = `${fontWeight} ${textSize}px ${fontFamily}`;
                    textWidth = ctx.measureText(text).width;
                    textHeight = textSize;
                }
            }
        
            let textX = customizationRegion.x + customizationRegion.width / 2;
            let textY = customizationRegion.y + iconHeight + padding;
        
            if (textOrientation === "vertical") {
                let translateY;
                if (iconUrl) {
                    const iconBottom = customizationRegion.y + (customizationRegion.height * 0.15) + iconHeight;
                    
                    translateY = iconBottom + (customizationRegion.height * 0.25);
                } else {
                    translateY = customizationRegion.y + (customizationRegion.height / 2);
                }
            
                // Clear any previous transformations
                ctx.setTransform(1, 0, 0, 1, 0, 0);
                
                ctx.translate(customizationRegion.x + customizationRegion.width / 2 + 10, translateY);
                ctx.rotate(-Math.PI / 2);
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                textX = 0;
                textY = 0;
            }
        
            if (fontPattern) {
                const patternImg = new Image();
                patternImg.crossOrigin = "anonymous";
                patternImg.onload = () => {
                    const patternCanvas = document.createElement("canvas");
                    const patternCtx = patternCanvas.getContext("2d");
                    patternCanvas.width = customizationRegion.width * 1.1;
                    patternCanvas.height = textHeight * 1.1;
                    patternCtx.drawImage(patternImg, 0, 0, customizationRegion.width * 1.1, textOrientation === "vertical" ? customizationRegion.height * 1.1 : textHeight * 1.1);
                    const pattern = ctx.createPattern(patternCanvas, "repeat");
                    ctx.fillStyle = pattern;
                    
                    const finalX = textOrientation === "vertical" ? textX : textX;
                    const finalY = textOrientation === "vertical" ? textY : textY;
                    
                    ctx.fillText(text, finalX, finalY);
                    // Restore context after drawing text
                    ctx.restore();
                };
                patternImg.onerror = () => {
                    console.error('❌ Failed to load font pattern, using white text');
                    ctx.fillStyle = "#fff";
                    
                    const finalX = textOrientation === "vertical" ? textX : textX;
                    const finalY = textOrientation === "vertical" ? textY : textY;
                    
                    ctx.fillText(text, finalX, finalY);
                    // Restore context after drawing text
                    ctx.restore();
                };
                patternImg.src = fontPattern;
            } else {
                ctx.fillStyle = "#fff";
                
                const finalX = textOrientation === "vertical" ? textX : textX;
                const finalY = textOrientation === "vertical" ? textY : textY;
                
                ctx.fillText(text, finalX, finalY);
                // Restore context after drawing text
                ctx.restore();
            }
        };
    
        if (iconUrl) {
            drawIcon().then(() => {
                drawText();
            });
        } else {
            drawText();
        }
    }
    
    debounce(func, delay) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => func.apply(this, args), delay);
        };
    }
}

if (!customElements.get('product-customizer')) {
    customElements.define('product-customizer', ProductCustomizer);
}